<script setup lang="ts">
import { toggleFavorite } from "@/api/services/BuildingProject";
import IconArea from "@/assets/Icons/Icon-Area.svg";
import defaultProfileImage from "@/assets/Images/defaultProfileImage.png";
import Dropdown from "@/components/Reusables/Dropdown/Dropdown.vue";
import DropdownItem from "@/components/Reusables/Dropdown/DropdownItem.vue";
import TagItem2 from "@/components/Reusables/Utils/TagItem.vue";
import InviteModalNew from "@/components/Utils/InviteUserToProject/InviteModalNew.vue";
import { DashboardEmissionBudgetType } from "@/features/Dashboard";
import { canEditProjectRole, canManageProjectRole, VIEWER_READ_ONLY_HINT } from "@/providers/Permissions/projectRoleAccess";
import { formatNumber } from "@/services/Utils/DecimalFormatter";
import { getUsedEmission as getUsedEmissionValue } from "@/services/Utils/emissionBudget";
import timeAgo from "@/services/Utils/timeAgo";
import User from "@/types/auth/User";
import { computed, ref } from "vue";
import router from "../../../router/router";
import ProjectCardResults from "./ProjectCardResults.vue";
import { materialMappingMenuLabel } from "../utils/materialMappingMenuLabel";
import { EmissionBudgetState } from "@/api/composables/useEmissionBudgets.composable";
import { GetProjectListQuery } from "@/graphql";
import FavoriteStarButton from "@/components/Reusables/Buttons/FavoriteStarButton.vue";
import RequestAccessModal from "@/components/Utils/RequestAccessToProject/RequestAccessModal.vue";
import SpeckleProjectImage from "@/components/Reusables/Image/SpeckleProjectImage.vue";
import Card from "@/components/Reusables/Card/Card.vue";
import CheckBox from "@/components/Reusables/Inputs/CheckBox.vue";
import { Bookmark, Lock, LockOpen } from "@lucide/vue";
import type { ProjectCompareItem } from "../composables/useProjectSelection";

const props = defineProps<{
  emissionBudget?: EmissionBudgetState;
  hasAccess: boolean;
  hasBeenSeen?: boolean;
  isSelectedForBulk?: boolean;
  preview?: boolean;
  project: GetProjectListQuery["project"][0];
  projectUserById?: Record<string, User>;
  projectUserLoading?: boolean;
  userId?: string;
}>();

const isFavorite = ref<boolean>(!!props.project.favorite);

// What this user may do with THIS project, from the membership role that travels on the row. Resolved
// once as computeds rather than by calling the permissions store from the template on every render,
// and shared by the menu's labels, its gating and its read-only hint (RTLCA-5583).
const canEditProject = computed(() => canEditProjectRole(props.project.role));
const canManageProject = computed(() => canManageProjectRole(props.project.role));

// Renamed, not hidden: the mapping page is readable with Viewer access, so the entry keeps working
// and stops promising an edit. The hint is the same sentence the mapping and waste surfaces use.
const materialMappingLabel = computed(() => materialMappingMenuLabel(canEditProject.value));

const materialMappingTooltip = computed(() => ({
  text: VIEWER_READ_ONLY_HINT,
  variant: "label" as const,
  placement: "bottom" as const,
  // Suppressed rather than unbound, so an Admin's menu never explains a control they can fully use.
  disabled: canEditProject.value,
}));

// The current revision (named save point) travels with the projectList fragment, so
// the list query AND the ProjectListUpdated subscription keep it live. Only its NAME is shown as a
// chip: the card title is the project's live name, because the current revision's state IS the
// working state — it holds no captured project name of its own (RTLCA-5270).
const activeRevisionName = computed(() => props.project.activeRevision?.name ?? "");
const displayName = computed(() => props.project.name);

// Country and calculation type, built only from what the project actually has. The old
// `?? "Denmark"` fallback stated a country the project may not be in whenever location was missing.
const projectMetaLine = computed(() =>
  [props.project.location?.country, props.project.calculation?.calculationType.label].filter(Boolean).join(" · ")
);

const inviteUsersModalIsHidden = ref<boolean>(true);
const requestAccessModalIsHidden = ref<boolean>(true);

const emit = defineEmits<{
  (e: "refetch"): void;
  (e: "compareItem", item: ProjectCompareItem): void;
}>();

// Toggle this card in/out of the compare set (mirrors LibraryMaterial's per-card compare checkbox).
const emitCompare = () => {
  emit("compareItem", { id: props.project.id, name: props.project.name });
};

// Avatar stack (bottom bar): show at most 4 overlapping avatars, roll the rest into a "+N" badge.
const MAX_VISIBLE_MEMBERSHIPS = 4;
const visibleMemberships = computed(() => props.project.memberships.slice(0, MAX_VISIBLE_MEMBERSHIPS));
const hiddenMembershipsCount = computed(() => Math.max(props.project.memberships.length - MAX_VISIBLE_MEMBERSHIPS, 0));

const specialConditionsEnabled = computed(() => {
  return !!props.project?.specialConditionConfig?.enabled || false;
});

const getUsedEmission = (emissionBudget: DashboardEmissionBudgetType) =>
  getUsedEmissionValue(emissionBudget, specialConditionsEnabled.value);

// BR18 2025 keeps A4-A5 outside the material budget, under its own threshold — the endpoint
// returns null for every other calculation type, which is what hides the second bar.
const transportInstallationBudget = computed(() => props.emissionBudget?.data?.transportInstallationBudget ?? null);

const transportInstallationSubLabel = computed(() => {
  const budget = transportInstallationBudget.value;
  if (!budget) return "";
  return `${formatNumber(budget.used.value)} / ${formatNumber(budget.threshold)}`;
});

const transportInstallationPercentage = computed(() => transportInstallationBudget.value?.used?.percentage ?? 0);

// Beside the A4-A5 bar the full wording clips in half a row, so the label is the bare figures, prefixed
// "Total:" so the pill still names what it covers (RTLCA-5479).
const emissionBudgetLabel = computed(() => {
  if (props.emissionBudget?.loading) return "Loading emissions...";
  const budget = props.emissionBudget?.data?.budget;
  if (!budget) return "";
  if (transportInstallationBudget.value) {
    return `Total: ${formatNumber(getUsedEmission(budget).value)} / ${formatNumber(budget.threshold)}`;
  }
  return `${formatNumber(budget.used.percentage)} % USED`;
});

const emissionBudgetSubLabel = computed(() => {
  const data = props.emissionBudget?.data;
  if (!data || transportInstallationBudget.value) return "";
  if (!data.budget || !data.unit) return "-";
  return `${formatNumber(getUsedEmission(data.budget).value)} / ${formatNumber(data.budget.threshold)}`;
});

// The unit the budget figures are measured in. Shown once in the caption row under the bars —
// out of the bar text, where it crowded the numbers (RTLCA-5419), but still beside the figures it
// describes: parked under the status chip it read as an orphaned technical label.
const emissionUnitLabel = computed(() => props.emissionBudget?.data?.unit?.label ?? "");

// Share of the budget used, resolved here rather than in the results block: which of the two figures
// applies is a property of the project (special conditions under BR18 2025), which the block does
// not carry.
const usedPercentage = computed(() => {
  const budget = props.emissionBudget?.data?.budget;
  return budget ? getUsedEmission(budget).percentage : 0;
});

const handleRefetch = () => {
  emit("refetch");
};

const handleToggleFavorite = async () => {
  isFavorite.value = !isFavorite.value;
  await toggleFavorite(props.project.id, isFavorite.value);
};

// The settings page, not the retired `/edit` wizard. Same destination the project's own sidebar
// offers, so the card and the sidebar agree on where a project's setup lives.
const navigateToProjectSettings = () => {
  router.push({ name: "projectSettings", params: { id: props.project.id } });
};

const navigateToMaterialMapping = () => {
  router.push({ name: "materialMappingView", params: { id: props.project.id } });
};

const toggleInviteUsersModal = () => {
  inviteUsersModalIsHidden.value = !inviteUsersModalIsHidden.value;
};

const toggleRequestAccessModal = () => {
  requestAccessModalIsHidden.value = !requestAccessModalIsHidden.value;
};
</script>

<template>
  <Card
    padding="none"
    :class="[
      'group/card text-type-default duration-normal relative flex h-full flex-col transition-all',
      isSelectedForBulk ? '!border-app-blue-600' : hasAccess ? 'shadow-xs hover:shadow-md' : 'shadow-xs hover:!border-slate-300',
    ]">
    <!-- Top bar: project name gets full width; compare affordance lives on the image below -->
    <div class="flex items-center justify-between gap-2 bg-slate-100 p-4">
      <div class="gap-px-2 flex min-w-0 flex-col">
        <span class="t-sh4 truncate">{{ displayName }}</span>
        <span class="t-footnote text-type-muted truncate">{{ timeAgo(project.updatedAt || project.createdAt) }}</span>
        <!-- The current revision — the save point everyone on the project works on. Always shown:
             `project.active_revision_id` is NOT NULL, and a project's first revision is named
             "Initial revision" by the Hasura `project_ensure_active_revision` trigger (the project's
             own name goes to a separate column), so the chip names a real save point from the moment
             the project exists. Always rendering it also keeps every card header the same height, so
             the cards in a row line up (RTLCA-5536). -->
        <span
          v-if="activeRevisionName"
          v-tooltip="{ text: `Current revision: ${activeRevisionName}`, variant: 'label' }"
          class="gap-px-4 t-footnote text-app-blue-900 flex min-w-0 items-center"
          data-cy="project-active-revision">
          <Bookmark
            class="icon-xs shrink-0"
            :stroke-width="1.5"
            color="var(--color-app-blue-800)"
            fill="var(--color-app-blue-100)" />
          <span class="truncate">{{ activeRevisionName }}</span>
        </span>
      </div>

      <div
        v-if="hasAccess"
        class="flex shrink-0 items-center gap-2">
        <FavoriteStarButton
          :is-favorite="isFavorite"
          @toggle-favorite="handleToggleFavorite" />
        <Dropdown>
          <DropdownItem
            v-tooltip="materialMappingTooltip"
            data-cy="go-to-mapping"
            @click="navigateToMaterialMapping">
            {{ materialMappingLabel }}
          </DropdownItem>
          <DropdownItem
            v-if="canEditProject"
            data-cy="edit-project"
            @click="navigateToProjectSettings">
            Project Settings
          </DropdownItem>
          <DropdownItem
            v-if="canManageProject"
            data-cy="manage-users"
            @click="toggleInviteUsersModal">
            Manage Users
          </DropdownItem>
        </Dropdown>
      </div>
      <div
        v-else
        class="flex shrink-0 items-center gap-2">
        <!-- locked -->
        <div class="group rounded-8 pointer-events-none relative flex h-7 w-7 items-center justify-center">
          <Lock
            class="icon-md pointer-events-none absolute text-slate-700/70"
            :stroke-width="1.5" />
        </div>
      </div>
    </div>

    <!-- Body: image area is a positioning context for the compare overlay, which sits as a sibling
         of the navigation <a> below so clicking it never triggers navigation. -->
    <div class="relative">
      <button
        v-if="hasAccess && !preview"
        type="button"
        :aria-pressed="!!isSelectedForBulk"
        aria-label="Add to comparison"
        :class="[
          'focus-ring interactive-active duration-fast ease-standard bg-slate-0/95 absolute top-3 left-3 z-10 flex items-center gap-2 rounded-full px-3 py-1.5 shadow-xs ring-1 transition-colors',
          isSelectedForBulk
            ? 'ring-app-blue-600 text-app-blue-700'
            : 'ring-slate-250 text-type-muted hover:text-type-default hover:ring-slate-300',
        ]"
        @click.stop="emitCompare">
        <!-- CheckBox is a visual-only indicator here: the button itself owns the click/aria-pressed
             state, so the box is hidden from assistive tech and pulled out of tab order. -->
        <CheckBox
          size="md"
          color="input"
          class="pointer-events-none"
          tabindex="-1"
          aria-hidden="true"
          :value="!!isSelectedForBulk"
          @update:value="() => undefined" />
        <span class="t-caption-bold">Compare</span>
      </button>

      <RouterLink
        v-if="hasAccess"
        v-slot="{ href, navigate }"
        custom
        :to="{ name: 'projectBreakdown', params: { id: project.id } }">
        <a
          :href="href"
          class="block select-none"
          @click="navigate">
          <!-- Image -->
          <SpeckleProjectImage
            height="h-48"
            :should-fetch="true"
            :stream-id="project.speckleStreamId || null" />

          <!-- Content. The lines stretch to this column's width; `items-end` sized them to their own
               text instead, so `truncate` clipped nothing and a long label ran across the status chip
               and out of the card (RTLCA-5807). The chip shares the META line rather than the whole
               block, so the building type keeps the full width — the Finnish types run to a sentence. -->
          <div class="gap-px-2 flex flex-col px-4">
            <p class="t-small-bold truncate">{{ project.buildingType.label }}</p>
            <!-- Country · calculation type. A project with neither renders this empty, and an empty
                 line collapses to height 0 — which is one of the two rows that made a card shorter
                 than its neighbours (RTLCA-5561). `min-h-px-16` floors it: `t-xs` measures 15.6px
                 (12px at leading-normal 1.3), so both the filled and the empty line land on 16px.
                 The row itself floors at the chip's own height, or a project carrying no status
                 would be shorter than its neighbours by the difference. -->
            <div class="min-h-px-20 flex items-center justify-between gap-x-2">
              <p class="t-xs text-type-muted min-h-px-16 min-w-0 truncate">
                {{ projectMetaLine }}
              </p>
              <TagItem2
                v-if="project.projectStatus"
                color="primary"
                class="shrink-0"
                :label="project.projectStatus.label" />
            </div>
          </div>
          <ProjectCardResults
            :emission-budget="emissionBudget"
            :emission-budget-label="emissionBudgetLabel"
            :emission-budget-sub-label="emissionBudgetSubLabel"
            :emission-unit-label="emissionUnitLabel"
            :transport-installation-budget="transportInstallationBudget"
            :transport-installation-percentage="transportInstallationPercentage"
            :transport-installation-sub-label="transportInstallationSubLabel"
            :used-percentage="usedPercentage"
            @refetch="handleRefetch" />
        </a>
      </RouterLink>
      <div
        v-else
        class="select-none">
        <SpeckleProjectImage
          height="h-48"
          :has-access="false"
          overflow-hidden
          is-overview
          :should-fetch="true"
          :stream-id="null" />

        <!-- Same content block as the branch above: see the comments there. -->
        <div class="gap-px-2 flex flex-col px-4">
          <p class="t-small-bold truncate">{{ project.buildingType.label }}</p>
          <!-- Country · calculation type. A project with neither renders this empty, and an empty
               line collapses to height 0 — which is one of the two rows that made a card shorter
               than its neighbours (RTLCA-5561). `min-h-px-16` floors it: `t-xs` measures 15.6px
               (12px at leading-normal 1.3), so both the filled and the empty line land on 16px.
               The row itself floors at the chip's own height, or a project carrying no status
               would be shorter than its neighbours by the difference. -->
          <div class="min-h-px-20 flex items-center justify-between gap-x-2">
            <p class="t-xs text-type-muted min-h-px-16 min-w-0 truncate">
              {{ projectMetaLine }}
            </p>
            <TagItem2
              v-if="project.projectStatus"
              color="primary"
              class="shrink-0"
              :label="project.projectStatus.label" />
          </div>
        </div>
        <ProjectCardResults
          :emission-budget="emissionBudget"
          :emission-budget-label="emissionBudgetLabel"
          :emission-budget-sub-label="emissionBudgetSubLabel"
          :emission-unit-label="emissionUnitLabel"
          :transport-installation-budget="transportInstallationBudget"
          :transport-installation-percentage="transportInstallationPercentage"
          :transport-installation-sub-label="transportInstallationSubLabel"
          :used-percentage="usedPercentage"
          @refetch="handleRefetch" />
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="t-sh5 mx-4 mb-4 flex items-center justify-between">
      <div class="flex items-center -space-x-2">
        <img
          v-for="user in visibleMemberships"
          :key="user.userId"
          class="border-slate-0 h-8 w-8 rounded-full border-2 object-cover"
          :src="projectUserById?.[user.userId]?.profilePictureUrl ?? defaultProfileImage"
          :alt="projectUserById?.[user.userId]?.name ?? 'Project member'" />
        <div
          v-if="hiddenMembershipsCount > 0"
          class="t-caption-bold border-slate-0 text-type-muted flex h-8 w-8 items-center justify-center rounded-full border-2 bg-slate-200">
          +{{ hiddenMembershipsCount }}
        </div>
      </div>
      <div class="flex gap-4">
        <div
          v-tooltip="{ text: 'Area', variant: 'label' }"
          class="flex items-center gap-2">
          <img
            :src="IconArea"
            alt="Area" />
          <span class="whitespace-nowrap">{{ `${formatNumber(project.area || 0)} m` }}<sup>2</sup></span>
        </div>
      </div>
    </div>

    <!-- Locked overlay (covers entire card). A real button rather than a clickable div, so the
         request-access flow is reachable by keyboard — the pill reveals on focus as well as hover. -->
    <button
      v-if="!hasAccess && !preview"
      type="button"
      aria-label="Request access to project"
      class="group/overlay backdrop-blur-0 rounded-4 duration-normal ease-standard focus-ring absolute inset-0 z-10 flex cursor-pointer items-center justify-center transition-all group-hover/card:backdrop-blur-[1px] focus-visible:backdrop-blur-[1px]"
      @click="toggleRequestAccessModal">
      <!-- Unlocked pill (appears on hover/focus) -->
      <span
        class="t-small-bold bg-slate-0/90 pointer-events-none absolute flex scale-0 items-center gap-2 rounded-full px-4 py-2 text-slate-700 opacity-0 shadow-lg transition-all duration-500 group-hover/card:scale-100 group-hover/card:opacity-100 group-focus-visible/overlay:scale-100 group-focus-visible/overlay:opacity-100">
        <LockOpen
          class="icon-xs text-app-yellow-500"
          :stroke-width="1.5" />
        <span>Request access to project</span>
      </span>
    </button>
  </Card>

  <InviteModalNew
    v-if="!inviteUsersModalIsHidden"
    :project="project"
    @toggle-invite-modal="toggleInviteUsersModal" />

  <RequestAccessModal
    v-if="!hasAccess && !preview && !requestAccessModalIsHidden"
    :emission-budget="emissionBudget"
    :project="project"
    @toggle-request-access-modal="toggleRequestAccessModal" />
</template>

<style scoped>
@reference "../../../style.css";

@keyframes skeleton-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.skeleton {
  @apply relative overflow-hidden rounded-[4px] bg-slate-300/20;
}

.skeleton::after {
  content: "";
  @apply absolute inset-0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: skeleton-shimmer 1.4s infinite;
}
</style>
